import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography,
} from '@mui/material';
import RangeList from './RangeList';
import { nextSevenDays, rangesProblem } from './weeklyHours';

// Admin view of a shift buyer's next 7 days (the buyer normally sets these on
// their own link page). A day with no range = no cards that day.
export default function ShiftsDialog({ buyer, onClose, onSave }) {
  const days = nextSevenDays();
  const [shifts, setShifts] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!buyer) return;
    setShifts(Object.fromEntries(nextSevenDays().map((d) => [d.key, buyer.dispatchShifts?.[d.key] || []])));
    setError(null);
  }, [buyer]);

  const save = async () => {
    const problem = rangesProblem(shifts);
    if (problem) {
      const day = days.find((d) => d.key === problem.key)?.en || problem.key;
      setError(problem.kind === 'closed'
        ? `${day}: a range runs into 02:00–07:00 — keep it inside 07:00–02:00.`
        : `${day}: time ranges overlap — make them not overlap, or remove one.`);
      return;
    }
    setError(null);
    setSaving(true);
    await onSave(shifts);
    setSaving(false);
  };

  return (
    <Dialog open={Boolean(buyer)} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Shifts — {buyer?.label}</DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 2 }}>
        <Typography variant="body2" color="text.secondary">
          The buyer sets these on their own link page; you can change them here too. Today and the next 6 days,
          07:00–02:00, 30-minute steps. A day with no range = no cards.
        </Typography>
        {error && <Alert severity="error">{error}</Alert>}
        <Box sx={{ display: 'grid', gap: 1.5 }}>
          {days.map((d) => (
            <Box key={d.key} sx={{ display: 'grid', gridTemplateColumns: '100px 1fr', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" fontWeight={600}>{d.en}</Typography>
              <RangeList
                idPrefix={`shift-${d.key}`}
                ranges={shifts[d.key] || []}
                onChange={(r) => setShifts((prev) => ({ ...prev, [d.key]: r }))}
              />
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving}>Save shifts</Button>
      </DialogActions>
    </Dialog>
  );
}
